import * as React from "react";
import { cn } from "@/lib/utils";

// Standardised page wrapper for the consumer site. Replaces the hand-rolled
// `<div className="pt-24 container mx-auto ...">` pattern that was creating
// 40% mobile screen dead-zones below the Navbar.
//
// The Navbar is sticky in document flow (not absolute over content), so no
// top spacer is needed. Vertical rhythm comes from the page's own sections.

type Variant = "default" | "narrow" | "wide" | "full" | "bleed";

const widthFor = (variant: Variant): string => {
  switch (variant) {
    case "narrow":
      return "max-w-3xl";
    case "wide":
      return "max-w-7xl";
    case "full":
      return "";
    case "bleed":
      return "";
    case "default":
    default:
      return "max-w-6xl";
  }
};

interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  // py controls vertical padding. Default is "default" (py-6 sm:py-10).
  // Use "tight" for pages where sections handle their own rhythm.
  py?: "default" | "tight" | "none";
}

const PageContainer = React.forwardRef<HTMLDivElement, PageContainerProps>(
  ({ variant = "default", py = "default", className, children, ...props }, ref) => {
    const pyClass =
      py === "none" ? "" : py === "tight" ? "py-4 sm:py-6" : "py-6 sm:py-10";
    return (
      <div
        ref={ref}
        className={cn(
          variant === "bleed" ? "w-full px-4 sm:px-6 lg:px-8" : "container mx-auto px-4 sm:px-6",
          widthFor(variant),
          pyClass,
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
PageContainer.displayName = "PageContainer";

export { PageContainer };
